import React, { useState, useEffect, useCallback, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Lock, Unlock, Zap, MapPin, LayoutDashboard,
  Calendar, BookOpen, ChevronRight, Gauge,
  Home, Route, Package, Play, Power, Cpu, Disc,
  Bell, ArrowUpRight, Plus, Link2, Activity, Wifi,
  AlertCircle, CheckCircle2, RefreshCw, WifiOff,
  BatteryCharging, Layers,
  Battery as BatteryIcon, Menu, X, TrendingUp, LogOut,
  List as ListIcon, Map as MapIcon,
} from 'lucide-react';
import Login from './Login';
import { authFetch, signOut, getCurrentUserInfo } from './auth';

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────
const API_BASE =
  (window._env_ && window._env_.REACT_APP_API_URL) ||
  process.env.REACT_APP_API_URL ||
  'https://api.slantconnect.com//api';
  // 'http://localhost:5000/api'; // local dev fallback

// ─────────────────────────────────────────────────────────────
// RESPONSIVE HOOK
// ─────────────────────────────────────────────────────────────
function useBreakpoint() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return { isMobile };
}

// ─────────────────────────────────────────────────────────────
// DATA HOOK
// ─────────────────────────────────────────────────────────────
function useApi(endpoint, pollMs = null) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(!!endpoint);
  const [error, setError]     = useState(null);

  const run = useCallback(async (silent = false) => {
    if (!endpoint) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}${endpoint}`);
      if (res.status === 401) { window.dispatchEvent(new Event('slant:unauthorized')); throw new Error('Session expired'); }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  // No endpoint yet (e.g. nothing selected) — stay idle instead of fetching.
  useEffect(() => {
    if (!endpoint) { setData(null); setLoading(false); return; }
    run();
    if (!pollMs) return;
    const id = setInterval(() => run(true), pollMs);
    return () => clearInterval(id);
  }, [run, pollMs, endpoint]);

  return { data, loading, error, refetch: run };
}

// ─────────────────────────────────────────────────────────────
// LOCK HOOK — POST to /api/lock → MQTT → ESP via IoT Core
// ─────────────────────────────────────────────────────────────
function useLock() {
  const [locked,  setLocked]  = useState(true);
  const [pending, setPending] = useState(false);
  const [error,   setError]   = useState(null);

  // Load initial state from backend on mount.
  // Only adopt the lock value — never start in a "pending/Sending…" state.
  // A request to the ESP is sent only when the user actually clicks the button.
  useEffect(() => {
    authFetch(`${API_BASE}/lock`)
      .then(r => r.json())
      .then(d => {
        setLocked(d.locked ?? true);
        setPending(false);
      })
      .catch(() => {}); // silently fail — use default
  }, []);

  // Poll for confirmation while pending
  useEffect(() => {
    if (!pending) return;
    const id = setInterval(async () => {
      try {
        const res = await authFetch(`${API_BASE}/lock`);
        const d   = await res.json();
        if (!d.pending) {
          setLocked(d.locked);
          setPending(false);
        }
      } catch (_) {}
    }, 2000);
    return () => clearInterval(id);
  }, [pending]);

  const toggle = async () => {
    if (pending) return;          // ignore tap while in-flight
    const newState = !locked;
    setPending(true);
    setError(null);

    try {
      const res = await authFetch(`${API_BASE}/lock`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ locked: newState, user: 'app' }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      // Optimistically update UI; confirmation will clear pending
      setLocked(data.locked);
      setPending(data.pending ?? false);
    } catch (e) {
      setError(e.message);
      setPending(false);
    }
  };

  return { locked, pending, error, toggle };
}

// ─────────────────────────────────────────────────────────────
// TINY SHARED COMPONENTS
// ─────────────────────────────────────────────────────────────
const SK = ({ w = '100%', h = 18, r = 6 }) => (
  <div style={{ width: w, height: h, borderRadius: r, background: 'linear-gradient(90deg,#eee 25%,#f5f5f5 50%,#eee 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
);

const Err = ({ msg, retry }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, marginBottom: 16 }}>
    <WifiOff size={15} color="#ef4444" />
    <span style={{ flex: 1, fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: '#ef4444' }}>{msg}</span>
    {retry && <button onClick={retry} style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: '#ef4444', background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}><RefreshCw size={10} />Retry</button>}
  </div>
);

const StatCard = ({ label, value, unit: u, color, loading }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: '18px 20px' }}>
    <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>{label}</div>
    {loading
      ? <SK h={28} r={6} />
      : <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 28, letterSpacing: '-0.03em', color: color || 'var(--text)' }}>
          {value ?? '—'}{u && <span style={{ fontSize: 13, color: 'var(--text-dim)', marginLeft: 4, fontWeight: 500 }}>{u}</span>}
        </div>}
  </div>
);

const SectionTitle = ({ children, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
    <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ display: 'inline-block', width: 12, height: 3, background: 'var(--accent)', borderRadius: 2 }} />
      {children}
    </div>
    {action}
  </div>
);

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────
const TUTORIALS = [
  { id: 1, title: 'Unboxing your flat-packed Conik M1', duration: '4:12', category: 'Setup' },
  { id: 2, title: 'EasyLink — attaching to your trailer', duration: '2:48', category: 'Hardware' },
  { id: 3, title: 'Swapping the battery module', duration: '1:08', category: 'Battery' },
];

const TABS = [
  { id: 'home',        label: 'Dashboard', Icon: LayoutDashboard },
  { id: 'trips',       label: 'Trips',     Icon: Route           },
  { id: 'charge',      label: 'Energy',    Icon: Zap             },
  { id: 'diagnostics', label: 'Diag',      Icon: Activity        },
  { id: 'unit',        label: 'Unit',      Icon: Package         },
];

// ─────────────────────────────────────────────────────────────
// LEAFLET MAP — real OpenStreetMap tiles (roads/labels) + route
// ─────────────────────────────────────────────────────────────
function LeafletMap({ points }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const layerRef     = useRef(null);

  // Create the map once.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: true,
    }).setView([51.4545, -2.5879], 12); // Bristol, UK default
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    mapRef.current = map;
    // Tiles can render at the wrong size if the container was hidden on mount.
    // Guard against StrictMode's mount→cleanup→mount: if this map instance
    // was already removed by the time the timeout fires, invalidateSize()
    // throws trying to read position off panes that no longer exist.
    const invalidateTimer = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(); }, 0);
    return () => {
      clearTimeout(invalidateTimer);
      map.remove();
      if (mapRef.current === map) mapRef.current = null;
    };
  }, []);

  // Draw / redraw the route whenever the points change.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null; }

    const latlngs = (points || [])
      .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lon))
      .map(p => [p.lat, p.lon]);
    if (!latlngs.length) return;

    const group = L.layerGroup();
    L.polyline(latlngs, { color: '#00d9ff', weight: 4, opacity: 0.9 }).addTo(group);
    latlngs.forEach(ll =>
      L.circleMarker(ll, { radius: 3, color: '#00d9ff', fillColor: '#00d9ff', fillOpacity: 0.7, weight: 1 }).addTo(group)
    );
    // Start marker (white)
    L.circleMarker(latlngs[0], { radius: 6, color: '#111', fillColor: '#fff', fillOpacity: 1, weight: 2 })
      .bindPopup('Start').addTo(group);
    // Latest position marker (accent)
    L.circleMarker(latlngs[latlngs.length - 1], { radius: 7, color: '#fff', fillColor: '#00d9ff', fillOpacity: 1, weight: 3 })
      .bindPopup('Latest position').addTo(group);

    group.addTo(map);
    layerRef.current = group;

    if (latlngs.length === 1) map.setView(latlngs[0], 15);
    else map.fitBounds(L.latLngBounds(latlngs).pad(0.15));
    const invalidateTimer = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(); }, 0);
    return () => clearTimeout(invalidateTimer);
  }, [points]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
}

// ─────────────────────────────────────────────────────────────
// TRIP MAP — Leaflet map + stat overlays
// ─────────────────────────────────────────────────────────────
function TripMap({ route, unit }) {
  const points   = route.points || [];
  const stats    = route.stats  || {};
  const count    = points.length;
  const hasRoute = count > 0;
  return (
    <div style={{ position: 'relative', height: '100%', borderRadius: 20, overflow: 'hidden', border: '1px solid var(--line-2)', background: '#f5f3f0' }}>
      <LeafletMap points={points} />
      {/* Map overlays (sit above Leaflet panes; don't block map gestures) */}
      <div style={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between', zIndex: 1000, pointerEvents: 'none' }}>
        <div style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 100, padding: '5px 10px', fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, background: '#00d9ff', borderRadius: '50%', animation: 'pulse 2s infinite' }} />
          {unit?.serial || 'CNK-0247'}
        </div>
        <div style={{ background: '#1a1a1a', color: '#fff', borderRadius: 100, padding: '5px 10px', fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 600 }}>{count} points</div>
      </div>
      {!hasRoute && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: '#737373', zIndex: 1000, pointerEvents: 'none' }}>
          No GPS points in range
        </div>
      )}
      {hasRoute && (
        <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(16px)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 14, padding: '12px 14px', zIndex: 1000, pointerEvents: 'none' }}>
          <div style={{ display: 'flex', gap: 20 }}>
            {[['Distance', stats.distance, 'mi'], ['Avg Speed', stats.avg_speed, 'mph'], ['Max Speed', stats.max_speed, 'mph'], ['Points', count, '']].map(([l, v, u]) => (
              <div key={l}>
                <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 8, color: '#737373', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{l}</div>
                <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 13 }}>{v ?? '—'}<span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: '#737373', marginLeft: 2 }}>{u}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TRIPS LIST — one row per calendar day, most recent first
// ─────────────────────────────────────────────────────────────
function TripsList({ trips, loading, onSelect }) {
  if (loading) return <div style={{ display: 'grid', gap: 10 }}>{[1,2,3,4].map(i => <SK key={i} h={72} r={16} />)}</div>;

  if (!trips || trips.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: 'JetBrains Mono,monospace', fontSize: 12, color: '#737373' }}>
        No trips in range
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {trips.map(t => (
        <div
          key={t.date}
          onClick={() => onSelect && onSelect(t.date)}
          style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', cursor: onSelect ? 'pointer' : 'default' }}
        >
          <div style={{ width: 48, flexShrink: 0 }}>
            <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t.day_label}</div>
            <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 15 }}>{t.date}</div>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[['Distance', t.distance, 'mi'], ['Avg speed', t.avg_speed, 'mph'], ['Max speed', t.max_speed, 'mph'], ['Points', t.points, '']].map(([l, v, u]) => (
              <div key={l}>
                <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 8, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{l}</div>
                <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 13 }}>{v ?? '—'}<span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', marginLeft: 2 }}>{u}</span></div>
              </div>
            ))}
          </div>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)' }}>{t.start_time}–{t.end_time}</div>
          {onSelect && <ChevronRight size={16} color="var(--text-dim)" />}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DIAGNOSTICS COMPONENT LIST (shared)
// ─────────────────────────────────────────────────────────────
function DiagList({ components, loading }) {
  const iconMap = { battery: BatteryIcon, cpu: Cpu, power: Power, zap: Zap, link: Link2, disc: Disc };
  if (loading) return <div style={{ display: 'grid', gap: 10 }}>{[1,2,3,4,5,6,7].map(i => <SK key={i} h={72} r={16} />)}</div>;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {(components || []).map(c => {
        const isOK = c.status === 'OK' || c.status === 'Idle';
        const isEmpty = c.status === 'Empty';
        const isErr = !isOK && !isEmpty;
        const Icon = iconMap[c.icon] || Activity;
        const iconBg = isEmpty ? 'rgba(0,0,0,0.04)' : isErr ? 'rgba(239,68,68,0.1)' : 'rgba(0,217,255,0.1)';
        const iconBd = isEmpty ? 'rgba(0,0,0,0.08)' : isErr ? 'rgba(239,68,68,0.3)' : 'rgba(0,217,255,0.3)';
        const iconCl = isEmpty ? '#a3a3a3' : isErr ? '#ef4444' : '#0891b2';
        const badgeBg = isEmpty ? 'rgba(0,0,0,0.06)' : isErr ? 'rgba(239,68,68,0.1)' : 'rgba(0,217,255,0.1)';
        const badgeCl = isEmpty ? '#737373' : isErr ? '#ef4444' : '#0891b2';
        return (
          <div key={c.id} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, border: `1px solid ${iconBd}`, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={16} strokeWidth={2.2} style={{ color: iconCl }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>{c.name}</div>
                <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 600, padding: '3px 7px', borderRadius: 6, textTransform: 'uppercase', letterSpacing: '0.06em', background: badgeBg, color: badgeCl }}>{c.status}</span>
              </div>
              {isOK    && <CheckCircle2 size={24} strokeWidth={2.5} color="#0891b2" />}
              {isEmpty && <AlertCircle  size={24} strokeWidth={2.5} color="#a3a3a3" />}
              {isErr   && <AlertCircle  size={24} strokeWidth={2.5} color="#ef4444" />}
            </div>
            {c.detail && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)', fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: '#737373' }}>{c.detail}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BATTERY RING
// ─────────────────────────────────────────────────────────────
function BatteryRing({ pct, range, maxRange, size = 200 }) {
  const r = 78; const circ = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={size} height={size} viewBox="0 0 220 220">
        <defs>
          <linearGradient id="rg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00d9ff" /><stop offset="100%" stopColor="#00b8d4" />
          </linearGradient>
        </defs>
        {Array.from({ length: 60 }).map((_, i) => {
          const a = (i / 60) * 2 * Math.PI - Math.PI / 2;
          return <line key={i} x1={110 + Math.cos(a) * 88} y1={110 + Math.sin(a) * 88} x2={110 + Math.cos(a) * (i % 5 === 0 ? 96 : 92)} y2={110 + Math.sin(a) * (i % 5 === 0 ? 96 : 92)} stroke="rgba(0,0,0,0.08)" strokeWidth="1" />;
        })}
        <circle cx="110" cy="110" r={r} fill="none" stroke="rgba(0,0,0,0.06)" strokeWidth="10" />
        <circle cx="110" cy="110" r={r} fill="none" stroke="url(#rg)" strokeWidth="10" strokeLinecap="round" strokeDasharray={`${(pct / 100) * circ} ${circ}`} transform="rotate(-90 110 110)" />
      </svg>
      <div style={{ position: 'absolute', textAlign: 'center' }}>
        <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: size * 0.3, letterSpacing: '-0.04em', lineHeight: 0.9, color: 'var(--text)' }}>
          {pct}<sup style={{ fontSize: size * 0.11, color: 'var(--accent-dark)', marginLeft: 2, verticalAlign: 'top', position: 'relative', top: size * 0.06 }}>%</sup>
        </div>
        <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.14em', marginTop: 4 }}>{range} of {maxRange} mi</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AUTH GATE — decides between Login and the Dashboard
// ─────────────────────────────────────────────────────────────
export default function SlantApp() {
  // null = still checking, false = signed out, object = signed in
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    const info = await getCurrentUserInfo();
    setUser(info || false);
    setChecking(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // A 401 from any API call (token rejected/expired) forces a re-login.
  useEffect(() => {
    const onUnauthorized = () => { signOut(); setUser(false); };
    window.addEventListener('slant:unauthorized', onUnauthorized);
    return () => window.removeEventListener('slant:unauthorized', onUnauthorized);
  }, []);

  const handleSignOut = () => { signOut(); setUser(false); };

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f3f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <RefreshCw size={26} color="#00d9ff" style={{ animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!user) return <Login onAuthenticated={refresh} />;

  return <Dashboard user={user} onSignOut={handleSignOut} />;
}

// ─────────────────────────────────────────────────────────────
// MAIN APP (authenticated)
// ─────────────────────────────────────────────────────────────
function Dashboard({ user, onSignOut }) {
  const { isMobile } = useBreakpoint();
  const [activeTab,    setActiveTab]    = useState('home');
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [tripView,     setTripView]     = useState('map'); // 'map' | 'list'
  const [selectedTripDate, setSelectedTripDate] = useState(null); // 'YYYY-MM-DD' or null

  // Lock state is fully managed by useLock (talks to /api/lock → MQTT → ESP)
  const { locked, pending: lockPending, error: lockError, toggle: toggleLock } = useLock();

  const unitApi     = useApi('/unit/status',     30000);
  const tripApi     = useApi('/trips?days=30',   null);
  const tripDaysApi = useApi('/trips/days?days=30', null);
  const tripDayApi  = useApi(selectedTripDate ? `/trips/day?date=${selectedTripDate}` : null, null);
  const batApi      = useApi('/battery',         30000);
  const diagApi     = useApi('/diagnostics',     60000);
  const locApi      = useApi('/location/current',30000);
  const statApi     = useApi('/trips/stats',     null);
  const histApi     = useApi('/battery/history', null);

  const unit  = unitApi.data  || {};
  const route = tripApi.data  || {};
  const trips = tripDaysApi.data?.trips || [];
  const bat   = batApi.data   || {};
  const diag  = diagApi.data  || {};
  const stats = statApi.data?.week || {};
  const hist  = histApi.data?.history || [];

  const b1pct = bat.battery1?.percentage ?? unit.battery ?? 72;
  const navigate = (tab) => { setActiveTab(tab); setSidebarOpen(false); };
  const userInitials = (user?.email || 'A')
    .split('@')[0]
    .split(/[._-]/)
    .map(s => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // ── shared tab content renderer ───────────────────────────
  const renderContent = () => {
    // ══ DASHBOARD ══
    if (activeTab === 'home') return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 4 }}>Good morning, Alex</div>
          <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: isMobile ? 24 : 32, letterSpacing: '-0.03em', color: 'var(--text)' }}>Dashboard</div>
        </div>

        {unitApi.error && <Err msg={unitApi.error} retry={unitApi.refetch} />}
        {unit.fault && !unitApi.loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 14, marginBottom: 20 }}>
            <AlertCircle size={18} color="#ef4444" />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 13, color: '#ef4444' }}>System fault detected</div>
              <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: '#737373', marginTop: 2 }}>Code {unit.fault_code} · Check Diagnostics</div>
            </div>
          </div>
        )}

        {/* ── Top row: hero + battery ── */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Hero card */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 24, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '0.02em' }}>CONIK M1</div>
                <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                  {unitApi.loading ? '…' : `${unit.nickname || 'TRAILER 02'} · ${unit.firmware || 'v2.4.1'}`}
                </div>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: 'rgba(0,217,255,0.1)', border: '1px solid rgba(0,217,255,0.3)', borderRadius: 100, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 600, color: '#0891b2' }}>
                <span style={{ width: 6, height: 6, background: '#00d9ff', borderRadius: '50%', animation: 'pulse 2s infinite' }} />
                {unitApi.loading ? '…' : unitApi.error ? 'Offline' : 'Online'}
              </div>
            </div>
            <div style={{ padding: '0 24px 8px', display: 'flex', justifyContent: 'center', height: 160, background: 'linear-gradient(180deg,#fafaf9 0%,#f5f3f0 100%)' }}>
              <img src="/conik_m1_w_skin.png" alt="Conik M1" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.12))' }} />
            </div>
            <div style={{ background: 'var(--surface-2)', padding: '16px 24px', borderTop: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Battery</div>
                  {unitApi.loading ? <SK h={48} w={90} /> : <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 52, letterSpacing: '-0.04em', lineHeight: 0.9 }}>{unit.battery ?? 72}<sup style={{ fontSize: 20, color: '#0891b2', verticalAlign: 'top', position: 'relative', top: 8 }}>%</sup></div>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Range left</div>
                  {unitApi.loading ? <SK h={28} w={80} /> : <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 26, letterSpacing: '-0.02em' }}>{unit.range ?? 58}<span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 3 }}>mi</span></div>}
                </div>
              </div>
            </div>
          </div>

          {/* Right column: quick actions + sensors */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

            {/* MQTT error banner */}
            {lockError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 12 }}>
                <WifiOff size={14} color="#ef4444" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: '#ef4444' }}>MQTT error: {lockError}</div>
              </div>
            )}

            {/* Lock + Find */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>

              {/* ── Lock / Unlock button ── */}
              <div
                onClick={lockPending ? undefined : toggleLock}
                title={`Click to ${locked ? 'unlock' : 'lock'} — publishes to aux_vo_London/sub`}
                style={{
                  background: !locked ? 'var(--text)' : 'var(--surface)',
                  border: `1px solid ${lockPending ? 'var(--accent)' : !locked ? 'var(--text)' : 'var(--line-2)'}`,
                  borderRadius: 16, padding: 16,
                  display: 'flex', alignItems: 'center', gap: 12,
                  cursor: lockPending ? 'wait' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: lockPending ? 0.8 : 1,
                  userSelect: 'none',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: lockPending
                    ? 'rgba(0,217,255,0.15)'
                    : !locked ? 'var(--accent)' : 'var(--surface-2)',
                  border: lockPending ? '1px solid rgba(0,217,255,0.4)' : 'none',
                  transition: 'all 0.2s',
                }}>
                  {lockPending
                    ? <RefreshCw size={15} strokeWidth={2.2} style={{ color: 'var(--accent-dark)', animation: 'spin 1s linear infinite' }} />
                    : locked
                      ? <Lock   size={15} strokeWidth={2.2} style={{ color: 'var(--text)' }} />
                      : <Unlock size={15} strokeWidth={2.2} style={{ color: 'var(--text)' }} />
                  }
                </div>
                <div>
                  <div style={{
                    fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 13,
                    color: !locked ? 'var(--bg)' : 'var(--text)',
                  }}>
                    {lockPending ? 'Sending…' : locked ? 'Locked' : 'Unlocked'}
                  </div>
                  <div style={{
                    fontFamily: 'JetBrains Mono,monospace', fontSize: 9, marginTop: 2,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                    color: !locked ? 'rgba(255,255,255,0.55)' : lockPending ? 'var(--accent-dark)' : 'var(--text-dim)',
                  }}>
                    {lockPending ? 'aux_vo_London/sub' : `lock · ${locked ? '1' : '0'}`}
                  </div>
                </div>
              </div>

              {/* ── Find unit ── */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: 16, padding: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Power size={15} strokeWidth={2.2} style={{ color: 'var(--text)' }} />
                </div>
                <div>
                  <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 13 }}>Find unit</div>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {unitApi.loading ? '…' : unit.sleeping ? 'Sleeping' : unit.speed_mph > 0 ? `${unit.speed_mph} mph` : 'Idle'}
                  </div>
                </div>
              </div>

            </div>

            {/* EasyLink */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Link2 size={16} strokeWidth={2.2} style={{ color: '#fff' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  EasyLink Connected <CheckCircle2 size={12} style={{ background: '#00d9ff', borderRadius: '50%', color: '#fff' }} />
                </div>
                <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>{unit.attachedTo || 'Velocity Trailer #14'}</div>
              </div>
              <ChevronRight size={16} color="var(--text-dim)" />
            </div>

            {/* Live sensors */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: 'var(--line)', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--line)' }}>
              {[['Speed', unitApi.loading ? null : unit.speed_mph ?? 0, 'mph'], ['Vibration', unitApi.loading ? null : unit.vibration != null ? Number(unit.vibration).toFixed(2) : '—', ''], ['Ride Score', unitApi.loading ? null : unit.imu_magnitude != null ? Number(unit.imu_magnitude).toFixed(2) : '—', 'g']].map(([l, v, u]) => (
                <div key={l} style={{ background: 'var(--surface)', padding: '14px 12px' }}>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 8, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{l}</div>
                  {unitApi.loading ? <SK h={20} /> : <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 20, letterSpacing: '-0.03em' }}>{v ?? '—'}{u && <span style={{ fontSize: 9, color: 'var(--text-dim)', marginLeft: 3 }}>{u}</span>}</div>}
                </div>
              ))}
            </div>

            {/* Axes */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: '14px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
              {[['X-axis', unit.x_axis], ['Y-axis', unit.y_axis], ['Z-axis', unit.z_axis]].map(([l, v], i) => (
                <div key={l} style={{ textAlign: i === 0 ? 'left' : i === 2 ? 'right' : 'center' }}>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 8, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{l}</div>
                  <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 15 }}>{unitApi.loading || v == null ? '—' : Number(v).toFixed(2)}<span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', marginLeft: 2 }}>g</span></div>
                </div>
              ))}
            </div>

            {/* Location */}
            <div style={{ background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: 14, padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <MapPin size={15} strokeWidth={2.2} style={{ color: '#fff' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 13 }}>{unit.location || 'Bristol, UK'}</div>
                <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>Updated {unit.lastUpdate || '—'}</div>
              </div>
              <ChevronRight size={16} color="var(--text-dim)" />
            </div>
          </div>
        </div>
      </div>
    );

    // ══ MAP / ROUTE ══
    if (activeTab === 'trips') return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 4 }}>
            {tripApi.loading ? '…' : `${route.points?.length ?? 0} points · last 30 days`}
          </div>
          <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: isMobile ? 24 : 32, letterSpacing: '-0.03em' }}>Route map.</div>
        </div>

        {tripApi.error && <Err msg={tripApi.error} retry={tripApi.refetch} />}

        {/* Stats strip — this week */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 20 }}>
          {[['Distance this week', stats.distance, 'mi']].map(([l, v, u]) => (
            <StatCard key={l} label={l} value={v} unit={u} loading={statApi.loading} />
          ))}
        </div>

        {/* Map / List subtabs */}
        <div style={{ display: 'inline-flex', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 4, gap: 4, marginBottom: 16 }}>
          {[['map', 'Map', MapIcon], ['list', 'List', ListIcon]].map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setTripView(id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: tripView === id ? 'var(--text)' : 'transparent',
                color: tripView === id ? 'var(--bg)' : 'var(--text-dim)',
                fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 12,
              }}
            >
              <Icon size={14} strokeWidth={2.2} />{label}
            </button>
          ))}
        </div>

        {tripView === 'map' ? (
          <div style={{ height: isMobile ? 360 : 600 }}>
            {tripApi.loading
              ? <div style={{ height: '100%', borderRadius: 20, background: 'var(--surface)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <RefreshCw size={22} color="#00d9ff" style={{ animation: 'spin 1s linear infinite' }} />
                </div>
              : <TripMap route={route} unit={unit} />}
          </div>
        ) : (
          <div>
            {tripDaysApi.error && <Err msg={tripDaysApi.error} retry={tripDaysApi.refetch} />}
            <TripsList trips={trips} loading={tripDaysApi.loading} onSelect={setSelectedTripDate} />
          </div>
        )}
      </div>
    );

    // ══ ENERGY ══
    if (activeTab === 'charge') return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 4 }}>Power & range</div>
          <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: isMobile ? 24 : 32, letterSpacing: '-0.03em' }}>Charge state.</div>
        </div>
        {batApi.error && <Err msg={batApi.error} retry={batApi.refetch} />}
        {b1pct < 20 && !batApi.loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(0,217,255,0.08)', border: '1px solid rgba(0,217,255,0.3)', borderRadius: 14, marginBottom: 20 }}>
            <AlertCircle size={16} color="#0891b2" />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 13 }}>Battery low</div>
              <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: '#737373', marginTop: 2 }}>Swap a fresh module or charge soon</div>
            </div>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Ring */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 24, padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            {batApi.loading ? <SK h={200} r={200} w={200} /> : <BatteryRing pct={b1pct} range={unit.range ?? 58} maxRange={unit.maxRange ?? 80} size={isMobile ? 180 : 220} />}
          </div>

          {/* Stats + chart */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: 'var(--line)', borderRadius: 16, overflow: 'hidden', border: '1px solid var(--line)' }}>
              {[['Per kWh', bat.efficiency ?? 28, 'mi'], ['Avg batt', hist.length > 0 ? Math.round(hist.reduce((s,h) => s+(h.avg_battery||0),0)/hist.length) : '—', '%'], ['Faults', hist.reduce((s,h) => s+(h.fault_count||0),0), '']].map(([l, v, u]) => (
                <div key={l} style={{ background: 'var(--surface)', padding: '16px 14px' }}>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 8, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{l}</div>
                  <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '-0.03em', color: l === 'Faults' && v > 0 ? '#ef4444' : 'var(--text)' }}>{v ?? '—'}{u && <span style={{ fontSize: 10, color: 'var(--text-dim)', marginLeft: 3 }}>{u}</span>}</div>
                </div>
              ))}
            </div>

            <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: 20, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
                <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Energy use</div>
                <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)' }}>Last 7 days</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 4, height: 100 }}>
                {histApi.loading
                  ? Array.from({length:7}).map((_,i) => <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}><SK h={`${30+Math.random()*60}%`} r={2} /><SK h={9} r={2} /></div>)
                  : (() => {
                      const DAYS = ['M','T','W','T','F','S','S'];
                      const padded = Array.from({length:7}).map((_,i) => hist[i] || { consumption: 0, day_label: DAYS[i] });
                      const maxC = Math.max(...padded.map(h => h.consumption || 0), 1);
                      const today = padded.length - 1;
                      return padded.map((h, i) => (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, height: '100%', justifyContent: 'flex-end' }}>
                          <div style={{ width: '100%', maxWidth: 22, height: `${((h.consumption||0)/maxC)*100}%`, background: i === today ? 'var(--accent)' : 'rgba(0,0,0,0.06)', borderRadius: 3, minHeight: 4, transition: 'height 0.3s' }} title={`${h.consumption||0}% consumed`} />
                          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)' }}>{h.day_label || DAYS[i]}</div>
                        </div>
                      ));
                    })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    // ══ DIAGNOSTICS ══
    if (activeTab === 'diagnostics') return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28 }}>
          <div>
            <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 4 }}>System health</div>
            <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: isMobile ? 24 : 32, letterSpacing: '-0.03em' }}>Component status.</div>
          </div>
          <button onClick={diagApi.refetch} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--surface)', border: '1px solid var(--line-2)', borderRadius: 10, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 10, fontWeight: 600, color: 'var(--text)' }}>
            <RefreshCw size={12} />Refresh
          </button>
        </div>
        {diagApi.error && <Err msg={diagApi.error} retry={diagApi.refetch} />}
        {/* Summary strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 24 }}>
          {[['Components', diag.total ?? 7, '', ''], ['Online', diag.online ?? 6, '', ''], ['Status', diag.status ?? 'OK', '', '#0891b2']].map(([l, v, u, color]) => (
            <StatCard key={l} label={l} value={v} unit={u} color={color} loading={diagApi.loading} />
          ))}
        </div>
        <SectionTitle>Components</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,1fr)', gap: 10 }}>
          <DiagList components={diag.components} loading={diagApi.loading} />
        </div>
        {diag.last_check && (
          <div style={{ marginTop: 20, padding: 16, background: 'var(--surface-2)', borderRadius: 12, border: '1px solid var(--line)', textAlign: 'center', fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: '#737373', lineHeight: 1.6 }}>
            Last system check: {new Date(diag.last_check).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}<br />Next scheduled check: in 58 minutes
          </div>
        )}
      </div>
    );

    // ══ UNIT ══
    if (activeTab === 'unit') return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: 4 }}>My equipment</div>
          <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: isMobile ? 24 : 32, letterSpacing: '-0.03em' }}>Conik M1.</div>
        </div>
        {unitApi.error && <Err msg={unitApi.error} retry={unitApi.refetch} />}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 24 }}>
          {/* Info table */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 24, padding: '18px 24px' }}>
            {unitApi.loading ? [1,2,3,4,5,6,7].map(i => <SK key={i} h={16} r={4} mb={16} />) :
              [['Model', 'Conik M1'], ['Serial', unit.serial || 'CNK-0247'], ['Nickname', unit.nickname || 'Trailer 02'], ['Total distance', `${(unit.odometer||1843).toLocaleString()} mi`], ['Firmware', unit.firmware || 'v2.4.1'], ['Drive ECU', 'OK', '#0891b2'], ['Aux ECU', 'OK', '#0891b2']].map(([l, v, c]) => (
                <div key={l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{l}</div>
                  <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontSize: 13, fontWeight: 600, color: c || 'var(--text)' }}>{v}</div>
                </div>
              ))}
          </div>

          {/* Service + tutorials */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <SectionTitle>Service & support</SectionTitle>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: '4px 16px' }}>
                {[[Calendar, 'Book a service', `Next due in ${unit.nextService || '157 mi'}`], [Activity, 'Diagnostics', 'All systems normal'], [Layers, 'Shells & accessories', 'Customise your unit'], [BookOpen, "Owner's handbook", 'Full documentation']].map(([Icon, title, sub]) => (
                  <div key={title} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}>
                    <div style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon size={14} strokeWidth={2.2} style={{ color: '#fff' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontSize: 13, fontWeight: 600 }}>{title}</div>
                      <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', marginTop: 2 }}>{sub}</div>
                    </div>
                    <ChevronRight size={16} color="var(--text-dim)" />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <SectionTitle action={<span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--accent-dark)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}>All<ArrowUpRight size={11} /></span>}>How-to videos</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {TUTORIALS.map(t => (
                  <div key={t.id} style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: 12, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Play size={15} fill="#fff" style={{ color: '#fff' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 8, color: 'var(--accent-dark)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600 }}>{t.category}</div>
                      <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontSize: 13, fontWeight: 600, marginTop: 2 }}>{t.title}</div>
                      <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', marginTop: 3 }}>{t.duration}</div>
                    </div>
                    <ChevronRight size={16} color="var(--text-dim)" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    return null;
  };

  // ─────────────────────────────────────────────────────────────
  // LAYOUT
  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: 'Inter,system-ui,sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        :root{
          --bg:#f5f3f0; --surface:#fff; --surface-2:#fafaf9;
          --line:rgba(0,0,0,0.08); --line-2:rgba(0,0,0,0.12);
          --text:#1a1a1a; --text-mid:#525252; --text-dim:#737373;
          --accent:#00d9ff; --accent-dim:#00b8d4; --accent-dark:#0891b2;
          --sidebar-w:220px;
        }
        @keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.8)}}
        body{background:var(--bg)}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.15);border-radius:2px}
        .sidebar-nav-btn{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:12px;cursor:pointer;border:none;background:transparent;width:100%;font-family:'Space Grotesk',sans-serif;font-size:13px;font-weight:600;color:var(--text-dim);transition:all .15s;text-align:left}
        .sidebar-nav-btn:hover{background:rgba(0,0,0,0.04);color:var(--text)}
        .sidebar-nav-btn.active{background:var(--text);color:var(--bg)}
        .sidebar-nav-btn.active .nav-icon{color:var(--accent)}
        .bottom-nav-btn{display:flex;flex-direction:column;align-items:center;gap:4px;padding:8px 4px;border:none;background:transparent;cursor:pointer;font-family:'Space Grotesk',sans-serif;font-size:9px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--text-dim);transition:color .2s;flex:1}
        .bottom-nav-btn.active{color:var(--text)}
        .bottom-nav-btn:hover:not(.active){color:var(--text-mid)}
      `}</style>

      {isMobile ? (
        /* ═══════════════════════════════════
           MOBILE LAYOUT
        ═══════════════════════════════════ */
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          {/* Mobile top bar */}
          <div style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--line)', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 50 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 20, letterSpacing: '0.06em' }}>SLANT</div>
              <div style={{ background: 'var(--text)', color: 'var(--bg)', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 5, letterSpacing: '0.06em' }}>{unit.serial || 'CNK-0247'}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', width: 36, height: 36, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <Bell size={15} strokeWidth={2.2} />
                <span style={{ position: 'absolute', top: 7, right: 7, width: 8, height: 8, background: 'var(--accent)', borderRadius: '50%', border: '2px solid var(--bg)' }} />
              </div>
              <button onClick={onSignOut} title="Sign out" style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <LogOut size={15} strokeWidth={2.2} style={{ color: 'var(--text)' }} />
              </button>
            </div>
          </div>

          {/* Mobile content */}
          <div style={{ flex: 1, padding: '20px 16px 90px', overflowY: 'auto' }}>
            {renderContent()}
          </div>

          {/* Mobile bottom nav */}
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', borderTop: '1px solid var(--line)', padding: '10px 4px 24px', display: 'flex', zIndex: 50 }}>
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} className={`bottom-nav-btn ${activeTab === id ? 'active' : ''}`} onClick={() => setActiveTab(id)}>
                <Icon size={17} strokeWidth={2.2} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* ═══════════════════════════════════
           DESKTOP LAYOUT
        ═══════════════════════════════════ */
        <div style={{ display: 'flex', minHeight: '100vh' }}>
          {/* Sidebar */}
          <div style={{ width: 'var(--sidebar-w)', background: 'var(--surface)', borderRight: '1px solid var(--line)', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 40 }}>
            {/* Logo */}
            <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid var(--line)' }}>
              <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 22, letterSpacing: '0.06em' }}>SLANT</div>
              <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)', marginTop: 4, letterSpacing: '0.04em' }}>{unit.serial || 'CNK-0247'}</div>
            </div>

            {/* Status chip */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: unitApi.error ? 'rgba(239,68,68,0.06)' : 'rgba(0,217,255,0.06)', borderRadius: 12, border: `1px solid ${unitApi.error ? 'rgba(239,68,68,0.2)' : 'rgba(0,217,255,0.2)'}` }}>
                <span style={{ width: 8, height: 8, background: unitApi.error ? '#ef4444' : '#00d9ff', borderRadius: '50%', animation: unitApi.error ? 'none' : 'pulse 2s infinite', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 12, color: 'var(--text)' }}>{unitApi.error ? 'Offline' : 'Online'}</div>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)', marginTop: 1 }}>Conik M1 · {unit.firmware || 'v2.4.1'}</div>
                </div>
                {!unitApi.loading && !unitApi.error && (
                  <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 13, color: 'var(--accent-dark)' }}>{unit.battery ?? 72}%</div>
                )}
              </div>
            </div>

            {/* Nav items */}
            <nav style={{ flex: 1, padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {TABS.map(({ id, label, Icon }) => (
                <button key={id} className={`sidebar-nav-btn ${activeTab === id ? 'active' : ''}`} onClick={() => setActiveTab(id)}>
                  <Icon size={16} strokeWidth={2.2} className="nav-icon" />
                  {label}
                </button>
              ))}
            </nav>

            {/* Sidebar footer */}
            <div style={{ padding: '16px', borderTop: '1px solid var(--line)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10, background: 'var(--surface-2)' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 12, color: 'var(--bg)' }}>{userInitials}</span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email || 'Account'}</div>
                  <div style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 9, color: 'var(--text-dim)' }}>Signed in</div>
                </div>
                <button onClick={onSignOut} title="Sign out" style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                  <LogOut size={14} strokeWidth={2.2} style={{ color: 'var(--text-mid)' }} />
                </button>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div style={{ marginLeft: 'var(--sidebar-w)', flex: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Top bar */}
            <div style={{ background: 'rgba(245,243,240,0.9)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--line)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 30 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {TABS.find(t => t.id === activeTab) && React.createElement(TABS.find(t => t.id === activeTab).Icon, { size: 18, strokeWidth: 2.2, style: { color: 'var(--text-dim)' } })}
                <span style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>{TABS.find(t => t.id === activeTab)?.label}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Live indicator */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 100, fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: 'var(--text-dim)' }}>
                  <span style={{ width: 6, height: 6, background: '#00d9ff', borderRadius: '50%', animation: 'pulse 2s infinite' }} />
                  Live · 30s refresh
                </div>
                <button onClick={unitApi.refetch} style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <RefreshCw size={15} strokeWidth={2.2} style={{ color: 'var(--text)' }} />
                </button>
                <div style={{ position: 'relative', width: 36, height: 36, borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  <Bell size={15} strokeWidth={2.2} />
                  <span style={{ position: 'absolute', top: 7, right: 7, width: 8, height: 8, background: 'var(--accent)', borderRadius: '50%', border: '2px solid var(--bg)' }} />
                </div>
              </div>
            </div>

            {/* Page content */}
            <div style={{ flex: 1, padding: '32px', maxWidth: 1200, width: '100%', margin: '0 auto' }}>
              {renderContent()}
            </div>
          </div>
        </div>
      )}

      {/* ── Trip detail modal: map of every point recorded on the selected day ── */}
      {selectedTripDate && (
        <div
          onClick={() => setSelectedTripDate(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 0 : 24 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--bg)', borderRadius: isMobile ? 0 : 20, width: '100%', maxWidth: 900, height: isMobile ? '100%' : '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
              <div style={{ fontFamily: 'Space Grotesk,sans-serif', fontWeight: 700, fontSize: 16 }}>{selectedTripDate}</div>
              <button onClick={() => setSelectedTripDate(null)} style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <X size={16} />
              </button>
            </div>
            <div style={{ flex: 1, padding: 16, minHeight: 0 }}>
              {tripDayApi.error
                ? <Err msg={tripDayApi.error} retry={tripDayApi.refetch} />
                : tripDayApi.loading
                  ? <div style={{ height: '100%', borderRadius: 20, background: 'var(--surface)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <RefreshCw size={22} color="#00d9ff" style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  : <TripMap route={tripDayApi.data || {}} unit={unit} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
